import { ArrowLeft, X } from "lucide-react";

import { ZButton } from "@/shared/components/button";
import { ZModal, ZModalBody, ZModalContent } from "@/shared/components/modal";
import { ZCheckbox } from "@/shared/components/checkbox";

interface ArchiveWarningModalProps {
  isOpen: boolean;
  isConfirmed: boolean;
  isLoading: boolean;
  loadingText?: string;
  onClose: () => void;
  onBack: () => void;
  onToggleConfirm: (confirmed: boolean) => void;
  onProceed: () => void;
}

export function ArchiveWarningModal({
  isOpen,
  isConfirmed,
  isLoading,
  loadingText,
  onClose,
  onBack,
  onToggleConfirm,
  onProceed,
}: ArchiveWarningModalProps) {
  return (
    <ZModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          onClose();
        }
      }}
      isDismissable={!isLoading}
      aria-label="Archive Warning"
      hideCloseButton
    >
      <ZModalContent
        className="!my-0 relative !w-[512px] !min-w-[512px] !max-w-[512px] overflow-hidden"
        style={{
          borderRadius: "16px",
          boxShadow: "0px 0px 24px rgba(0, 0, 0, 0.05)",
        }}
      >
        {/* X Close button - absolutely positioned */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute hover:opacity-70 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed z-10"
          style={{
            right: "16px",
            top: "16px",
            width: "16px",
            height: "16px",
          }}
          aria-label="Close"
        >
          <X
            className="text-[#62748E]"
            style={{ width: "16px", height: "16px" }}
            strokeWidth={2}
          />
        </button>

        <ZModalBody
          className="flex flex-col items-center"
          style={{ padding: "24px", gap: "24px" }}
        >
          {/* Container - Header + Subtitle with 16px gap */}
          <div
            className="flex flex-col items-center w-full"
            style={{ gap: "16px" }}
          >
            {/* Header row - Back button + Title */}
            <div
              className="flex flex-row items-start w-full"
              style={{ gap: "8px", height: "28px" }}
            >
              {/* Back Button Container */}
              <button
                onClick={onBack}
                disabled={isLoading}
                className="flex items-center justify-center hover:opacity-70 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                style={{
                  width: "20px",
                  height: "28px",
                  padding: "4px 0px",
                }}
                aria-label="Go back"
              >
                <ArrowLeft
                  className="text-[#0F172B]"
                  style={{ width: "20px", height: "20px" }}
                  strokeWidth={1.5}
                />
              </button>

              {/* Title */}
              <h2
                className="font-semibold text-center flex-1"
                style={{
                  fontSize: "18px",
                  lineHeight: "28px",
                  color: "#0F172B",
                }}
              >
                Archive Existing Assessment?
              </h2>
            </div>

            {/* Subtitle */}
            <p
              className="text-center w-full"
              style={{
                fontSize: "14px",
                lineHeight: "20px",
                color: "#314158",
              }}
            >
              Starting a new assessment will automatically archive and lock your
              current assessment.
            </p>
          </div>

          {/* Frame 7134 - Checkbox section with button */}
          <div
            className="flex flex-col items-start w-full"
            style={{ gap: "16px" }}
          >
            {/* Frame 6068 - Checkbox row */}
            <div
              className="flex flex-row items-start w-full"
              style={{ gap: "8px" }}
            >
              <ZCheckbox
                isSelected={isConfirmed}
                onValueChange={onToggleConfirm}
                isDisabled={isLoading}
                radius="sm"
                classNames={{
                  base: "max-w-full",
                  wrapper:
                    "!rounded-[6px] after:!rounded-[4px] before:!rounded-[6px]",
                  label:
                    "text-[14px] leading-5 text-[#314158] whitespace-nowrap pl-2",
                }}
              >
                I understand the current assessment will be archived and locked.
              </ZCheckbox>
            </div>

            {/* Button */}
            <ZButton
              color="primary"
              className="w-full"
              style={{
                height: "40px",
                padding: "8px 16px",
                backgroundColor: "#006FEE",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: 500,
                lineHeight: "24px",
              }}
              isDisabled={!isConfirmed || isLoading}
              isLoading={isLoading}
              onPress={onProceed}
            >
              {isLoading ? loadingText || "Processing..." : "Proceed"}
            </ZButton>
          </div>
        </ZModalBody>
      </ZModalContent>
    </ZModal>
  );
}
